import React from 'react';
import { Play, Heart, X, ChevronLeft, ChevronRight, Film, Grid3X3 } from 'lucide-react';
import { supabase } from '../supabaseClient';

const PAGE_SIZE = 12;
const mediaUrl = value => {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; }
  catch { return ''; }
};
const dateLabel = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

function HighlightTile({ highlight, onOpen }) {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: '120px' });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const url = mediaUrl(highlight.video_url);
  return <button ref={ref} className="athlete-highlight-tile" onClick={onOpen} aria-label={`Watch ${highlight.description || highlight.sport_id || 'athlete'} highlight`}>
    {visible && url && !failed
      ? <video src={`${url}${url.includes('#') ? '' : '#t=0.1'}`} muted playsInline preload="metadata" aria-hidden="true" tabIndex={-1} onError={() => setFailed(true)} />
      : <div className="athlete-highlight-placeholder"><Film size={28} /><span>{highlight.sport_id || 'Highlight'}</span></div>}
    <span className="athlete-highlight-play"><Play size={16} fill="currentColor" aria-hidden="true" /></span>
    <span className="athlete-highlight-tile-meta"><span>{highlight.sport_id || 'ProAm'}</span><span><Heart size={12} aria-hidden="true" />{Number(highlight.like_count || 0).toLocaleString()}</span></span>
  </button>;
}

export default function PublicProfileHighlights({ profile, appLink, onOpenApp }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [restricted, setRestricted] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [retry, setRetry] = React.useState(0);
  const [selected, setSelected] = React.useState(null);
  const [playbackFailed, setPlaybackFailed] = React.useState(false);
  const dialogRef = React.useRef(null);
  const active = selected == null ? null : rows[selected];
  React.useEffect(() => {
    let mounted = true;
    setLoading(true); setError('');
    const load = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        let hiddenIds = new Set();
        if (session?.user?.id) {
          const [viewer, hidden] = await Promise.all([
            supabase.from('profiles').select('account_type').eq('id', session.user.id).maybeSingle(),
            supabase.from('hidden_highlights').select('highlight_id').eq('profile_id', session.user.id),
          ]);
          if (viewer.error || hidden.error) throw viewer.error || hidden.error;
          if (String(viewer.data?.account_type || '').toLowerCase() === 'student') {
            if (mounted) { setRestricted(true); setRows([]); }
            return;
          }
          hiddenIds = new Set((hidden.data || []).map(item => item.highlight_id));
        }
        const { data, error: queryError } = await supabase.from('highlights')
          .select('id,video_url,description,like_count,created_at,sport_id')
          .eq('profile_id', profile.id).order('created_at', { ascending: false }).order('id', { ascending: false })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (queryError) throw queryError;
        if (mounted) {
          const visible = (data || []).filter(item => !hiddenIds.has(item.id));
          setRows(previous => page === 0 ? visible : [...new Map([...previous, ...visible].map(item => [item.id, item])).values()]);
          setHasMore(data?.length === PAGE_SIZE);
        }
      } catch {
        if (mounted) setError('Highlights could not be loaded. Please try again.');
      } finally { if (mounted) setLoading(false); }
    };
    load();
    return () => { mounted = false; };
  }, [profile.id, page, retry]);
  React.useEffect(() => {
    const dialog = dialogRef.current;
    if (!active || !dialog) return;
    if (!dialog.open) dialog.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [active]);
  const close = () => { dialogRef.current?.close(); setSelected(null); };
  const choose = index => { setPlaybackFailed(false); setSelected(index); };
  return <section className="athlete-highlights" aria-labelledby="athlete-highlights-title">
    <div className="athlete-highlights-heading"><h3 id="athlete-highlights-title"><Grid3X3 size={18} aria-hidden="true" /> Highlights</h3><span>IN THEIR ELEMENT</span></div>
    {restricted ? <p className="athlete-highlight-status">Highlights are unavailable for Student accounts.</p> : <>
      {rows.length > 0 && <div className="athlete-highlight-grid">{rows.map((highlight, index) => <HighlightTile key={highlight.id} highlight={highlight} onOpen={() => choose(index)} />)}</div>}
      {loading && <div className="athlete-highlight-status" role="status">Loading highlights…</div>}
      {error && <div className="athlete-highlight-status" role="alert">{error}<button onClick={() => setRetry(n => n + 1)}>Try again</button></div>}
      {!loading && !error && !rows.length && <div className="athlete-highlight-empty"><Film size={28} aria-hidden="true" /><h4>No highlights yet</h4><p>Their sporting moments will appear here when shared.</p></div>}
      {hasMore && !loading && !error && <button className="athlete-highlights-more" onClick={() => setPage(n => n + 1)}>Show more highlights</button>}
    </>}
    <dialog ref={dialogRef} className="athlete-highlight-dialog" aria-label={`Highlight by @${profile.username}`} onClose={() => setSelected(null)} onClick={event => { if (event.target === event.currentTarget) close(); }}>
      {active && <div className="athlete-highlight-dialog-actions"><button className="athlete-highlight-close" onClick={close} aria-label="Close highlight"><X size={23} /></button></div>}
      {active && <div className="athlete-highlight-viewer">
        <div className="athlete-highlight-player">
          {!playbackFailed && mediaUrl(active.video_url) ? <video key={active.id} src={mediaUrl(active.video_url)} controls playsInline autoPlay onError={() => setPlaybackFailed(true)} aria-label={active.description || `${active.sport_id || 'Athlete'} highlight`} />
            : <div className="athlete-highlight-status" role="status">This video could not play. You can try viewing it in the app.</div>}
        </div>
        <div className="athlete-highlight-caption">
          <div className="athlete-highlight-author"><strong>@{profile.username}</strong><span>{active.sport_id || 'Snows ProAm'}</span></div>
          {active.description && <p>{active.description}</p>}
          <div className="athlete-highlight-stats"><span><Heart size={16} aria-hidden="true" />{Number(active.like_count || 0).toLocaleString()} likes</span><time dateTime={active.created_at}>{dateLabel(active.created_at)}</time></div>
          <a className="athlete-primary" href={appLink} onClick={onOpenApp}>Connect with this athlete in the app</a>
          <p className="athlete-highlight-app-note">Open Snows ProAm to like, comment and connect.</p>
          <div className="athlete-highlight-paging"><button onClick={() => choose(selected - 1)} disabled={selected === 0} aria-label="Previous highlight"><ChevronLeft size={20} /></button><span>{selected + 1} / {rows.length} loaded</span><button onClick={() => choose(selected + 1)} disabled={selected === rows.length - 1} aria-label="Next highlight"><ChevronRight size={20} /></button></div>
        </div>
      </div>}
    </dialog>
  </section>;
}
